import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Cpu, Zap, DollarSign, Shield, Clock, 
  Activity, Gauge, Thermometer, Wind, Server,
  TrendingUp, AlertTriangle, Info, ChevronDown 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMinerDetails } from '../hooks/useMinerDetails';
import { useMarketData } from '../hooks/useMarketData';
import { ProfitAnalysis } from '../components/ProfitAnalysis';
import { MiningRewards } from '../components/MiningRewards';
import { HistoricalProfitability } from '../components/HistoricalProfitability';
import { ROIAnalysis } from '../components/ROIAnalysis';
import { Loader } from '../components/common/Loader';
import { calculateProfitability } from '../utils/calculations';
import { formatCurrency } from '../utils/currencyUtils';
import type { ExchangeRates } from '../utils/currencyUtils';

interface MinerDetailsProps {
  currency: string;
  exchangeRates: ExchangeRates;
  powerCost: number;
}

export function MinerDetails({ currency, exchangeRates, powerCost }: MinerDetailsProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { miner, isLoading: minerLoading, error: minerError } = useMinerDetails(id);
  const { data: marketData, isLoading: marketLoading } = useMarketData(miner?.coin || '');
  const [showSpecs, setShowSpecs] = React.useState(false);
  const [purchasePrice, setPurchasePrice] = React.useState(5000);
  const [writeoffMonths, setWriteoffMonths] = React.useState(24);

  const isLoading = minerLoading || marketLoading;

  const profitability = React.useMemo(() => {
    if (!miner || !marketData) return null;
    return calculateProfitability(miner, { [miner.coin]: marketData }, powerCost, currency, exchangeRates);
  }, [miner, marketData, powerCost, currency, exchangeRates]);

  if (isLoading) {
    return <Loader />;
  }

  if (minerError || !miner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Miner Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Calculator
          </button>
        </div>
      </div>
    );
  }

  const isProfitable = profitability?.dailyProfit > 0;
  const breakEvenMonths = purchasePrice / (profitability?.monthlyProfit || 1);
  const totalReturn = (profitability?.monthlyProfit || 0) * writeoffMonths;
  const netProfit = totalReturn - purchasePrice;
  const totalROI = (netProfit / purchasePrice) * 100;
  const annualROI = ((profitability?.monthlyProfit || 0) * 12 / purchasePrice) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      {/* Rest of the component remains unchanged until ROI Analysis section */}
      
      <ROIAnalysis
        purchasePrice={purchasePrice}
        monthlyProfit={profitability?.monthlyProfit || 0}
        writeoffMonths={writeoffMonths}
        breakEvenMonths={breakEvenMonths}
        totalReturn={totalReturn}
        netProfit={netProfit}
        totalROI={totalROI}
        annualROI={annualROI}
        currency={currency}
        exchangeRates={exchangeRates}
        onPurchasePriceChange={setPurchasePrice}
        onWriteoffMonthsChange={setWriteoffMonths}
      />
      
      {/* Rest of the component remains unchanged */}
    </div>
  );
}